import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Box,
  CircularProgress,
  Grid,
  IconButton,
  ModalProps,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import Hls from "hls.js";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";

import { BaseModal } from "./BaseModal";
import { FlexCol } from "../FlexCol";
import { FlexRow } from "../FlexRow";
import { LoadingViewInComponent } from "../LoadingViewInComponent";
import { userSelector } from "../../store/auth/selector";
import { syncUserAction } from "../../store/auth/actions";
import { socketSelector } from "../../store/socket/selector";
import { ReactComponent as CopyIcon } from "../../assets/svgs/copy.svg";
import { ReactComponent as ViewIcon } from "../../assets/svgs/view.svg";
import ZoomIcon from "../../assets/pngs/zoom.png";
import StreamYardIcon from "../../assets/pngs/streamYard.png";
import ObsIcon from "../../assets/pngs/obs.png";
import RestreamIcon from "../../assets/pngs/restream.png";
import IvsPlayer from "../LiveStreamPlayer/IvsPlayer";

interface Props extends Partial<ModalProps> {
  open: boolean;
  onClose: () => void;
  onNext: () => void;
  onBack: () => void;
  data?: any;
}

export const StreamPreviewModal = ({ open, onClose, onNext, onBack, ...props }: Props) => {
  const dispatch = useDispatch();
  const user = useSelector(userSelector);
  const { socketConnected, notification } = useSelector(socketSelector);
  const [showStreamKey, setShowStreamKey] = useState(false);
  const [loadedVideo, setLoadedVideo] = useState(false);
  const playerRef = useRef<HTMLVideoElement>(null);
  // const [streamKey, setStreamKey] = useState("");
  const [streamUrl, setStreamUrl] = useState("");

  // const streamUrl = `rtmps://${user?.streamProfile?.streamEndpoint}:443/app/` || "";
  // const streamKey = user?.streamProfile?.streamKey || "";
  // const streamLink = `${document.location.origin}/${user?.username}`;
  // const playUrl = user?.streamProfile?.url || "";

  const zoomHelp =
    "https://support.zoom.us/hc/en-us/articles/115001777826-Livestreaming-meetings-or-webinars-on-a-custom-site";
  const obsHelp = "https://resources.antmedia.io/docs/open-broadcaster-software-publishing";
  const restreamHelp = "https://support.restream.io/en/articles/369436-connect-a-custom-rtmp-channel";
  const streamyardHelp = "https://streamyard.com/blog/how-to-live-stream/custom-rtmp-live-streaming/";

  const copyText = (text: string) => {
    navigator.clipboard.writeText(text || "");
  };

  useEffect(() => {
    if (socketConnected && notification?.channelId === user?.id) {
      dispatch(syncUserAction());
    }
  }, [notification]);

  const [streamKey, setStreamKey] = useState("");
  // State to hold the Livepeer Studio playback URL
  const [playbackUrl, setPlaybackUrl] = useState("");
  // State to control whether the video player is visible and streaming is active
  const [isStreaming, setIsStreaming] = useState(false);
  // State to hold a title for the stream
  const [streamTitle, setStreamTitle] = useState("My Live Stream");
  // State to manage the visibility of the instructions modal
  const [showInstructions, setShowInstructions] = useState(false);
  // State to track if the video is loading
  const [videoLoading, setVideoLoading] = useState(false);
  // State to store video error messages
  const [videoError, setVideoError] = useState("");

  // Ref for the video element to interact with it directly
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // Ref for the Hls.js instance
  const hlsRef = useRef<Hls | null>(null);

  // Effect to load saved stream key and playback URL from local storage on component mount
  useEffect(() => {
    const savedStreamKey = localStorage.getItem("livepeerStreamKey");
    const savedPlaybackUrl = localStorage.getItem("livepeerPlaybackUrl");
    const savedStreamTitle = localStorage.getItem("livepeerStreamTitle");

    if (savedStreamKey) setStreamKey(savedStreamKey);
    if (savedPlaybackUrl) setPlaybackUrl(savedPlaybackUrl);
    if (savedStreamTitle) setStreamTitle(savedStreamTitle);
  }, []);

  // Effect to handle HLS.js initialization and playback
  useEffect(() => {
    if (isStreaming && playbackUrl && videoRef.current) {
      setVideoLoading(true); // Start loading indicator
      setVideoError(""); // Clear previous errors

      if (hlsRef.current) {
        hlsRef.current.destroy(); // Destroy existing Hls instance if any
      }

      // Check if HLS is supported by the browser
      if (Hls.isSupported()) {
        const hls = new Hls();
        hlsRef.current = hls; // Store Hls instance in ref

        // Bind Hls.js to the video element
        hls.attachMedia(videoRef.current);

        hls.on(Hls.Events.MEDIA_ATTACHED, () => {
          hls.loadSource(playbackUrl); // Load the HLS manifest
        });

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          // Autoplay muted video once manifest is parsed
          if (videoRef.current) {
            videoRef.current.play().catch((error) => {
              console.error("Video autoplay failed:", error);
              setVideoLoading(false);
              setVideoError("Autoplay prevented. Please click play.");
              // User might need to interact with the video for it to play
            });
          }
        });

        // Handle HLS.js errors
        hls.on(Hls.Events.ERROR, (event, data) => {
          setVideoLoading(false);
          console.error("HLS.js error:", data);
          let errorMessage = "An HLS playback error occurred.";
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                errorMessage = `Network error: ${data.details}. Please check URL or network connection.`;
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                errorMessage = `Media error: ${data.details}. Check stream encoding.`;
                break;
              default:
                errorMessage = `Fatal HLS error: ${data.details}.`;
                break;
            }
            // Attempt to recover from errors if possible
            if (data.fatal) {
              if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
                hls.recoverMediaError();
              } else if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
                hls.startLoad();
              } else {
                hls.destroy();
              }
            }
          } else {
            errorMessage = `Non-fatal HLS error: ${data.details}.`;
          }
          setVideoError(`Error: ${errorMessage}`);
        });

        // Event listener for when video actually starts playing
        videoRef.current.onplay = () => {
          setVideoLoading(false);
          setVideoError("");
        };
      } else if (videoRef.current.canPlayType("application/vnd.apple.mpegurl")) {
        // Fallback for browsers that support HLS natively (e.g., Safari)
        videoRef.current.src = playbackUrl;
        videoRef.current.play().catch((error) => {
          console.error("Native video autoplay failed:", error);
          setVideoLoading(false);
          setVideoError("Autoplay prevented. Please click play.");
        });
      } else {
        // If neither HLS.js nor native HLS is supported
        setVideoLoading(false);
        setVideoError("Your browser does not support HLS video playback.");
      }
    } else if (!isStreaming && hlsRef.current) {
      // Clean up Hls instance when not streaming
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    // Cleanup function for useEffect
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [isStreaming, playbackUrl]); // Re-run effect when these dependencies change

  // Function to handle starting the stream
  const handleStartStream = () => {
    // Reset any previous video errors
    setVideoError("");
    // Set video loading state
    setVideoLoading(true);
    // Save current values to local storage
    localStorage.setItem("livepeerStreamKey", streamKey);
    localStorage.setItem("livepeerPlaybackUrl", playbackUrl);
    localStorage.setItem("livepeerStreamTitle", streamTitle);
    setIsStreaming(true); // Set streaming to true to display the video player
  };

  // Function to handle stopping the stream
  const handleStopStream = () => {
    setIsStreaming(false); // Set streaming to false to hide the video player
    setVideoLoading(false); // Reset loading state
    setVideoError(""); // Clear any errors
  };

  // Icon for a video camera
  const CameraIcon = (props: any) => (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
      <circle cx="12" cy="13" r="3" />
    </svg>
  );

  // Icon for a play button
  const PlayIcon = (props: any) => (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="5 3 19 12 5 21 5 3"></polygon>
    </svg>
  );

  // Icon for a stop button
  const StopIcon = (props: any) => (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="6" y="6" width="12" height="12"></rect>
    </svg>
  );

  // Icon for information
  const InfoIcon = (props: any) => (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10"></circle>
      <line x1="12" y1="16" x2="12" y2="12"></line>
      <line x1="12" y1="8" x2="12.01" y2="8"></line>
    </svg>
  );
  return (
    <BaseModal
      {...props}
      open={open}
      onClose={onClose}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backdropFilter: "blur(7px)",
      }}
    >
      <Box
        component={Paper}
        className="-webkit-chatbox-scrollbar"
        sx={{
          py: 2,
          borderRadius: 6,
          position: "relative",
          width: 468,
          height: "90%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Grid container spacing={2} sx={{ overflow: "auto", flex: 1, px: 2, pt: 1, pb: 4 }}>
          <Grid container item alignItems="center" spacing={1} py={2}>
            <Grid item xs="auto">
              <IconButton onClick={onBack} size="small">
                <ArrowBackIosNewRoundedIcon sx={{ cursor: "pointer", opacity: 0.5 }} />
              </IconButton>
            </Grid>
            <Grid item xs>
              <Typography
                sx={{
                  color: "#A3AED0",
                  fontWeight: 600,
                  fontSize: { xs: 18, md: 20 },
                  lineHeight: "30px",
                }}
              >
                Create stream
              </Typography>
            </Grid>
            <Grid item xs="auto">
              <Button
                variant="contained"
                sx={{ textTransform: "none", height: 34, borderRadius: 29 / 2, px: 4.5 }}
                onClick={onNext}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: 14, lineHeight: "18.55px" }}>
                  Next
                </Typography>
              </Button>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2} px={2}>
              <Grid item xs={12}>
                <Typography sx={{ color: "#A3AED0", fontWeight: 400, fontSize: 10, lineHeight: "15px", mt: 2.5 }}>
                  Stream Preview
                </Typography>
              </Grid>
              <Grid item xs={12}>
                {videoRef ? (
                  <Box
                    sx={{
                      position: "relative",
                      backgroundColor: "black",
                      marginTop: -1,
                      minHeight: { xs: 160, sm: 200 },
                    }}
                  >
                    {/* <IvsPlayer
                      ref={playerRef}
                      style={{ transition: "all 0.8s ease" }}
                      height="100%"
                      width="100%"
                      url={playUrl}
                      isLowLatency
                      muted
                      playing
                      onPlay={() => setLoadedVideo(true)}
                    /> */}
                    <video
                      ref={videoRef} // Attach ref to video element
                      controls
                      autoPlay
                      muted // Muted to prevent autoplay policy issues
                      className="absolute top-0 left-0 w-full h-full rounded-xl"
                      // onPlay and onError are now handled by Hls.js events or directly on videoRef
                    >
                      Your browser does not support the video tag.
                    </video>
                    {/* <LoadingViewInComponent visible={!videoRef} sx={{ backgroundColor: "#00000080" }} /> */}
                  </Box>
                ) : (
                  <FlexCol
                    sx={{
                      backgroundColor: "#000",
                      minHeight: "215px",
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      mt: -1,
                    }}
                  >
                    <CircularProgress />
                    <Typography sx={{ fontWeight: 300, fontSize: 10, lineHeight: "15px", mt: 3 }}>
                      Connect streaming software to go live
                    </Typography>
                  </FlexCol>
                )}
              </Grid>
              {/* <Grid item xs={12}>
                <Typography sx={{ color: "#A3AED0", fontWeight: 400, fontSize: 12, lineHeight: "18px", mt: 1 }}>
                  Server URL
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FlexRow sx={{ position: "relative" }}>
                  <TextField
                    value={streamUrl}
                    name="url"
                    inputProps={{
                      style: {
                        backgroundColor: "#282C46",
                        border: "0.5px solid rgba(255, 255, 255, 0.15)",
                        fontWeight: 400,
                        fontSize: 14,
                        lineHeight: "21px",
                        height: "20px",
                        padding: "7px",
                        paddingLeft: "13px",
                        paddingRight: "80px",
                        borderRadius: "6px",
                      },
                    }}
                    fullWidth
                  />
                  <FlexRow sx={{ alignItems: "center" }}>
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: "rgba(60, 67, 111, 1)",
                        ":hover": { backgroundColor: "rgba(0, 0, 0, 0.2)" },
                        position: "absolute",
                        right: 4,
                        height: "23.57px",
                      }}
                      onClick={() => copyText(streamUrl)}
                    >
                      <CopyIcon />
                      <Typography sx={{ fontWeight: 400, fontSize: 7, lineHeight: "10.5px", ml: 1 }}>COPY</Typography>
                    </Button>
                  </FlexRow>
                </FlexRow>
              </Grid> */}
              <Grid item xs={12}>
                <Typography sx={{ color: "#A3AED0", fontWeight: 400, fontSize: 12, lineHeight: "18px", mt: 1 }}>
                  Stream Key
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FlexRow sx={{ position: "relative" }}>
                  <TextField
                    value={streamKey}
                    onChange={(e) => setStreamKey(e.target.value)}
                    type={showStreamKey ? "text" : "password"}
                    name="username"
                    inputProps={{
                      style: {
                        backgroundColor: "#282C46",
                        border: "0.5px solid rgba(255, 255, 255, 0.15)",
                        fontWeight: 400,
                        fontSize: 14,
                        lineHeight: "21px",
                        height: "20px",
                        padding: "7px",
                        paddingLeft: "13px",
                        paddingRight: "170px",
                        borderRadius: "6px",
                      },
                    }}
                    fullWidth
                    // onChange={(e) => setStreamKey(e.target.value)}
                  />
                  <FlexRow sx={{ alignItems: "center" }}>
                    <Typography
                      sx={{ cursor: "pointer", position: "absolute", right: "80px", mt: 0.5 }}
                      onClick={() => setShowStreamKey(!showStreamKey)}
                    >
                      <ViewIcon />
                    </Typography>
                    {/* <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "rgba(60, 67, 111, 1)",
                    ":hover": { backgroundColor: "rgba(0, 0, 0, 0.2)" },
                    position: "absolute",
                    right: 80,
                    height: "23.57px",
                  }}
                >
                  <Typography sx={{ fontWeight: 400, fontSize: 7, lineHeight: "10.5px" }}>RESET</Typography>
                </Button> */}
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: "rgba(60, 67, 111, 1)",
                        ":hover": { backgroundColor: "rgba(0, 0, 0, 0.2)" },
                        position: "absolute",
                        right: 4,
                        height: "23.57px",
                      }}
                      onClick={() => copyText(streamKey)}
                    >
                      <CopyIcon />
                      <Typography sx={{ fontWeight: 400, fontSize: 7, lineHeight: "10.5px", ml: 1 }}>COPY</Typography>
                    </Button>
                  </FlexRow>
                </FlexRow>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  sx={{ color: "#A3AED0", fontWeight: 400, fontSize: 12, lineHeight: 1.5, mb: 0.5 }}
                >
                  Playback URL
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FlexRow sx={{ position: "relative" }}>
                  <TextField
                    name="url"
                    type="url"
                    value={playbackUrl}
                    onChange={(e) => setPlaybackUrl(e.target.value)}
                    inputProps={{
                      style: {
                        backgroundColor: "#282C46",
                        border: "0.5px solid rgba(255, 255, 255, 0.15)",
                        fontWeight: 400,
                        fontSize: 14,
                        lineHeight: "21px",
                        height: "20px",
                        padding: "7px",
                        paddingLeft: "13px",
                        paddingRight: "80px",
                        borderRadius: "6px",
                      },
                    }}
                    fullWidth
                    // onChange={(e) => setStreamUrl(e.target.value)}
                  />
                  <FlexRow sx={{ alignItems: "center" }}>
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: "rgba(60, 67, 111, 1)",
                        ":hover": { backgroundColor: "rgba(0, 0, 0, 0.2)" },
                        position: "absolute",
                        right: 4,
                        height: "23.57px",
                      }}
                      onClick={() => copyText(streamUrl)}
                    >
                      <CopyIcon />
                      <Typography sx={{ fontWeight: 400, fontSize: 7, lineHeight: "10.5px", ml: 1 }}>COPY</Typography>
                    </Button>
                  </FlexRow>
                </FlexRow>
              </Grid>
              <Grid>
                <Button
                  onClick={() => handleStartStream()}
                  // disabled={!streamKey || !playbackUrl}
                  className="flex items-center gap-2 px-8 py-4 bg-purple-600 text-white font-bold text-lg rounded-2xl shadow-lg hover:bg-purple-700 transition duration-300 ease-in-out transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PlayIcon className="w-6 h-6" /> Start Streaming
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  sx={{
                    color: "#A3AED0",
                    fontWeight: 600,
                    fontSize: 14,
                    lineHeight: "21px",
                    textAlign: "center",
                    mt: 1,
                  }}
                >
                  Learn how to stream through clients below
                </Typography>
                <FlexRow sx={{ maxWidth: "244px", mx: "auto", flexWrap: "wrap", justifyContent: "center", py: 2 }}>
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "rgba(60, 67, 111, 1)",
                      ":hover": { backgroundColor: "rgba(0, 0, 0, 0.2)" },
                      height: "36px",
                      width: "107px",
                      borderRadius: "10.42px",
                    }}
                    onClick={() => window.open(zoomHelp)}
                  >
                    <img src={ZoomIcon} alt="" />
                  </Button>
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "rgba(60, 67, 111, 1)",
                      ":hover": { backgroundColor: "rgba(0, 0, 0, 0.2)" },
                      height: "36px",
                      width: "107px",
                      borderRadius: "10.42px",
                      ml: 1,
                    }}
                    onClick={() => window.open(streamyardHelp)}
                  >
                    <img src={StreamYardIcon} alt="" />
                  </Button>
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "rgba(60, 67, 111, 1)",
                      ":hover": { backgroundColor: "rgba(0, 0, 0, 0.2)" },
                      height: "36px",
                      width: "107px",
                      borderRadius: "10.42px",
                      mt: 2,
                    }}
                    onClick={() => window.open(obsHelp)}
                  >
                    <img src={ObsIcon} alt="" />
                  </Button>
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "rgba(60, 67, 111, 1)",
                      ":hover": { backgroundColor: "rgba(0, 0, 0, 0.2)" },
                      height: "36px",
                      width: "107px",
                      borderRadius: "10.42px",
                      mt: 2,
                      ml: 1,
                    }}
                    onClick={() => window.open(restreamHelp)}
                  >
                    <img src={RestreamIcon} alt="" />
                  </Button>
                </FlexRow>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </BaseModal>
  );
};
